<template>
  <v-dialog v-model="dialog" max-width="500" persistent>
    <v-card class="rounded-xl">
      <v-card-title class="pa-6 pb-4">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="40" class="mr-4">
            <v-icon color="white">mdi-account-plus</v-icon>
          </v-avatar>
          <div>
            <h3 class="text-h5 font-weight-bold mb-0">
              {{ isEntrada ? "Registrar Entrada" : "Registrar Salida" }}
            </h3>
            <p class="text-caption text--secondary mb-0">
              {{
                isEntrada
                  ? "Crear nuevo registro de acceso"
                  : "Registrar salida del visitante"
              }}
            </p>
          </div>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="isEntrada"
                :label="isEntrada ? 'Entrada' : 'Salida'"
                color="primary"
                inset
                class="mb-3"
              >
                <template v-slot:prepend>
                  <v-icon :color="isEntrada ? 'green' : 'red'">
                    {{ isEntrada ? "mdi-login" : "mdi-logout" }}
                  </v-icon>
                </template>
              </v-switch>
            </v-col>

            <!-- Botón Scanner -->
            <v-col cols="12" class="pt-0">
              <v-btn
                v-if="!showScanner"
                @click="toggleScanner"
                color="primary"
                outlined
                block
                class="mb-3 rounded-lg"
                :disabled="loading"
              >
                <v-icon left>mdi-qrcode-scan</v-icon>
                Scanner
              </v-btn>

              <!-- Input Scanner -->
              <v-text-field
                v-if="showScanner"
                ref="scannerInput"
                v-model="scannerValue"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-qrcode-scan"
                label="Escanear código"
                placeholder="Mantenga el cursor aquí y escanee"
                class="rounded-lg mb-3"
                @keyup.enter="processScannerInput"
              >
                <template v-slot:append>
                  <v-btn @click="closeScanner" icon small color="grey">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.dni"
                :rules="dniRules"
                outlined
                dense
                required
                prepend-inner-icon="mdi-card-account-details"
                label="DNI *"
                placeholder="Ingrese el número de DNI"
                type="number"
                maxlength="8"
                class="rounded-lg"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="formData.nombre"
                outlined
                :rules="nombreRules"
                dense
                prepend-inner-icon="mdi-account"
                label="Nombre y Apellido *"
                placeholder="Ingrese el nombre completo"
                class="rounded-lg"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="formData.email"
                outlined
                dense
                prepend-inner-icon="mdi-email"
                label="Email (opcional)"
                placeholder="Ingrese el email"
                type="email"
                class="rounded-lg"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="formData.motivo"
                outlined
                dense
                prepend-inner-icon="mdi-text"
                label="Motivo (opcional)"
                placeholder="Motivo de la visita"
                class="rounded-lg"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-6">
        <v-spacer></v-spacer>
        <v-btn
          text
          color="grey darken-1"
          class="mr-3 rounded-lg"
          @click="closeDialog"
          :disabled="loading"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          depressed
          class="rounded-lg font-weight-bold"
          elevation="2"
          @click="submitForm"
          :loading="loading"
          :disabled="!valid"
        >
          <v-icon left small>{{
            isEntrada ? "mdi-login" : "mdi-logout"
          }}</v-icon>
          {{ isEntrada ? "Registrar Entrada" : "Registrar Salida" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "RegistrationDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      valid: false,
      loading: false,
      isEntrada: true, // true para entrada, false para salida
      showScanner: false,
      scannerValue: "",
      formData: {
        dni: "",
        nombre: "",
        email: "",
        motivo: "Evento (Registro Manual)",
      },
      dniRules: [
        (v) => !!v || "El DNI es requerido",
        (v) => (v && v.length >= 7) || "El DNI debe tener al menos 7 dígitos",
        (v) => (v && v.length <= 8) || "El DNI debe tener máximo 8 dígitos",
        (v) => /^\d+$/.test(v) || "El DNI debe contener solo números",
      ],
      nombreRules: [(v) => !!v || "El nombre no puede estar vacío"],
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    async submitForm() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const payload = {
          dni: this.formData.dni,
          entrada: this.isEntrada ? 1 : 0, // 1 para entrada, 0 para salida
        };

        // Agregar campos opcionales solo si tienen valor
        if (this.formData.nombre.trim()) {
          payload.nombre = this.formData.nombre.trim();
        }
        if (this.formData.email.trim()) {
          payload.email = this.formData.email.trim();
        }
        if (this.formData.motivo.trim()) {
          payload.motivo = this.formData.motivo.trim();
        }

        const response = await axios.post(
          process.env.VUE_APP_BACKEND_ENVIRONMENT + "acreditar",
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.status === 200) {
          this.$emit("success", {
            message:
              response.data.mensaje ||
              `${
                this.isEntrada ? "Entrada" : "Salida"
              } registrada correctamente`,
            data: response.data,
          });
          this.resetForm();
          this.closeDialog();
        }
      } catch (error) {
        console.error("Error al registrar entrada:", error);
        this.$emit("error", {
          message:
            error.response?.data?.mensaje ||
            `Error al registrar la ${this.isEntrada ? "entrada" : "salida"}`,
          error: error,
        });
      } finally {
        this.loading = false;
      }
    },
    toggleScanner() {
      this.showScanner = true;
      this.$nextTick(() => {
        if (this.$refs.scannerInput) {
          this.$refs.scannerInput.focus();
        }
      });
    },
    closeScanner() {
      this.showScanner = false;
      this.scannerValue = "";
    },
    processScannerInput() {
      if (this.scannerValue.trim()) {
        try {
          const datosPersonales = {};
          let datos;

          if (
            this.scannerValue.startsWith("0") &&
            this.scannerValue.split('"').length >= 8
          ) {
            // Procesar el primer tipo de código
            datos = this.scannerValue.split('"');
            console.log("datos en el if", datos);

            if (datos.length >= 5) {
              // Asegura que hay suficientes partes en el array
              datosPersonales.dni = datos[4];
              datosPersonales.nombre = datos[1] + " " + datos[2];
            } else {
              throw new Error(
                "Formato inválido para el primer tipo de código."
              );
            }
          } else if (this.scannerValue.split('"').length >= 6) {
            // Procesar el segundo tipo de código
            datos = this.scannerValue.split('"');
            console.log("datos en el else", datos);

            if (datos.length >= 6) {
              // Asegura que hay suficientes partes en el array
              datosPersonales.dni = datos[1].trim();
              datosPersonales.nombre = datos[4] + " " + datos[5];
            } else {
              throw new Error(
                "Formato inválido para el segundo tipo de código."
              );
            }
          } else {
            throw new Error(
              "El código proporcionado no coincide con ningún formato válido."
            );
          }

          // Validar los datos extraídos
          const dniValido = /^\d{7,8}$/.test(datosPersonales.dni); // Verifica si el DNI es numérico y tiene entre 7 u 8 dígitos
          const nombreValido =
            datosPersonales.nombre &&
            datosPersonales.nombre.trim().length > 0 &&
            /^[a-zA-Z\s]+$/.test(datosPersonales.nombre);

          if (dniValido && nombreValido) {
            // Si los datos son válidos, llenar los campos del formulario
            this.formData.dni = datosPersonales.dni;
            this.formData.nombre = datosPersonales.nombre.trim();

            console.log("Datos extraídos:", {
              dni: datosPersonales.dni,
              nombre: datosPersonales.nombre,
            });

            this.closeScanner();
          } else {
            throw new Error("Los datos extraídos del código no son válidos.");
          }
        } catch (error) {
          console.error("Error al procesar código:", error);
          // Si hay error, mostrar el valor crudo en el DNI como fallback
          this.formData.dni = this.scannerValue.trim();
          this.closeScanner();

          // Opcional: mostrar un mensaje de error al usuario
          this.$emit("error", {
            message: "Error al procesar el código: " + error.message,
            error: error,
          });
        }
      }
    },
    resetForm() {
      // No resetear isEntrada para mantener la selección del usuario
      this.showScanner = false;
      this.scannerValue = "";
      this.formData = {
        dni: "",
        nombre: "",
        email: "",
        motivo: "Evento (Registro Manual)",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    closeDialog() {
      this.resetForm();
      this.dialog = false;
    },
  },
  watch: {
    dialog(val) {
      if (val) {
        // Solo resetear los campos del formulario, no el switch
        this.showScanner = false;
        this.scannerValue = "";
        this.formData = {
          dni: "",
          nombre: "",
          email: "",
          motivo: "Evento (Registro Manual)",
        };
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      }
    },
  },
};
</script>

<style scoped></style>
